import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon } from 'lucide-react';
import { ExchangeRate, ExchangeRateType, EntityScope } from '../CurrencyExchangeView';
import { EntitySiteSelector } from './EntitySiteSelector';
interface ExchangeRateFormProps {
  rate: ExchangeRate | null;
  onSave: (rate: ExchangeRate) => void;
  onCancel: () => void;
  locale: string;
}
export function ExchangeRateForm({
  rate,
  onSave,
  onCancel,
  locale
}: ExchangeRateFormProps) {
  const [formData, setFormData] = useState<Partial<ExchangeRate>>({
    id: '',
    fromCurrency: '',
    toCurrency: '',
    rate: 1,
    inverseRate: 1,
    type: 'Manual' as ExchangeRateType,
    scope: 'Global' as EntityScope,
    isActive: true,
    lastUpdated: ''
  });
  // Load data if editing an existing rate
  useEffect(() => {
    if (rate) {
      setFormData({
        ...rate
      });
    }
  }, [rate]);
  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    // Handle checkbox
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    // Handle rate input
    if (name === 'rate') {
      const rateValue = parseFloat(value);
      if (!isNaN(rateValue) && rateValue > 0) {
        setFormData(prev => ({
          ...prev,
          rate: rateValue,
          inverseRate: 1 / rateValue
        }));
      }
      return;
    }
    // Handle other fields
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle entity/site selection
  const handleEntitySiteChange = (entityId: string | undefined, entityName: string | undefined, siteId: string | undefined, siteName: string | undefined, scope: EntityScope) => {
    setFormData(prev => ({
      ...prev,
      entityId,
      entityName,
      siteId,
      siteName,
      scope
    }));
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Calculate inverse rate if not set
    const rateValue = formData.rate || 1;
    const inverseRateValue = formData.inverseRate || 1 / rateValue;
    // Prepare data for saving
    const rateToSave: ExchangeRate = {
      id: formData.id || '',
      fromCurrency: formData.fromCurrency || '',
      toCurrency: formData.toCurrency || '',
      rate: rateValue,
      inverseRate: inverseRateValue,
      type: formData.type as ExchangeRateType,
      entityId: formData.entityId,
      entityName: formData.entityName,
      siteId: formData.siteId,
      siteName: formData.siteName,
      scope: formData.scope as EntityScope,
      isActive: formData.isActive || false,
      lastUpdated: formData.lastUpdated || new Date().toISOString(),
      updatedBy: 'Current User'
    };
    onSave(rateToSave);
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-7719">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-7720">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7721">
          {rate ? 'Edit Exchange Rate' : 'Add Exchange Rate'}
        </h3>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7722">
          <XIcon className="h-5 w-5" data-id="element-7723" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-7724">
        {/* Currency Pair */}
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-7725">
          <div className="sm:col-span-3" data-id="element-7726">
            <label htmlFor="fromCurrency" className="block text-sm font-medium text-gray-700" data-id="element-7727">
              From Currency
            </label>
            <select id="fromCurrency" name="fromCurrency" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={formData.fromCurrency} onChange={handleChange} required data-id="element-7728">
              <option value="" data-id="element-7729">Select currency</option>
              <option value="USD" data-id="element-7730">USD - US Dollar</option>
              <option value="EUR" data-id="element-7731">EUR - Euro</option>
              <option value="GBP" data-id="element-7732">GBP - British Pound</option>
              <option value="CAD" data-id="element-7733">CAD - Canadian Dollar</option>
              <option value="AUD" data-id="element-7734">AUD - Australian Dollar</option>
              <option value="JPY" data-id="element-7735">JPY - Japanese Yen</option>
              <option value="CNY" data-id="element-7736">CNY - Chinese Yuan</option>
              <option value="INR" data-id="element-7737">INR - Indian Rupee</option>
              <option value="BRL" data-id="element-7738">BRL - Brazilian Real</option>
              <option value="MXN" data-id="element-7739">MXN - Mexican Peso</option>
              <option value="ZAR" data-id="element-7740">ZAR - South African Rand</option>
              <option value="RUB" data-id="element-7741">RUB - Russian Ruble</option>
              <option value="SGD" data-id="element-7742">SGD - Singapore Dollar</option>
            </select>
          </div>
          <div className="sm:col-span-3" data-id="element-7743">
            <label htmlFor="toCurrency" className="block text-sm font-medium text-gray-700" data-id="element-7744">
              To Currency
            </label>
            <select id="toCurrency" name="toCurrency" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={formData.toCurrency} onChange={handleChange} required data-id="element-7745">
              <option value="" data-id="element-7746">Select currency</option>
              <option value="USD" data-id="element-7747">USD - US Dollar</option>
              <option value="EUR" data-id="element-7748">EUR - Euro</option>
              <option value="GBP" data-id="element-7749">GBP - British Pound</option>
              <option value="CAD" data-id="element-7750">CAD - Canadian Dollar</option>
              <option value="AUD" data-id="element-7751">AUD - Australian Dollar</option>
              <option value="JPY" data-id="element-7752">JPY - Japanese Yen</option>
              <option value="CNY" data-id="element-7753">CNY - Chinese Yuan</option>
              <option value="INR" data-id="element-7754">INR - Indian Rupee</option>
              <option value="BRL" data-id="element-7755">BRL - Brazilian Real</option>
              <option value="MXN" data-id="element-7756">MXN - Mexican Peso</option>
              <option value="ZAR" data-id="element-7757">ZAR - South African Rand</option>
              <option value="RUB" data-id="element-7758">RUB - Russian Ruble</option>
              <option value="SGD" data-id="element-7759">SGD - Singapore Dollar</option>
            </select>
          </div>
        </div>
        {/* Rate Value */}
        <div data-id="element-7760">
          <label htmlFor="rate" className="block text-sm font-medium text-gray-700" data-id="element-7761">
            Exchange Rate
          </label>
          <div className="mt-1 relative rounded-md shadow-sm" data-id="element-7762">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7763">
              <span className="text-gray-500 sm:text-sm" data-id="element-7764">
                1 {formData.fromCurrency || '---'} =
              </span>
            </div>
            <input type="number" name="rate" id="rate" step="0.0001" min="0.0001" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-16 pr-12 sm:text-sm border-gray-300 rounded-md" placeholder="0.0000" value={formData.rate || ''} onChange={handleChange} required data-id="element-7765" />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-7766">
              <span className="text-gray-500 sm:text-sm" data-id="element-7767">
                {formData.toCurrency || '---'}
              </span>
            </div>
          </div>
          {formData.rate && formData.rate > 0 && <p className="mt-2 text-sm text-gray-500" data-id="element-7768">
              Inverse: 1 {formData.toCurrency || '---'} ={' '}
              {(1 / formData.rate).toFixed(4)} {formData.fromCurrency || '---'}
            </p>}
        </div>
        {/* Rate Type */}
        <div data-id="element-7769">
          <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-7770">
            Rate Type
          </label>
          <select id="type" name="type" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={formData.type} onChange={handleChange} required data-id="element-7771">
            <option value="Spot" data-id="element-7772">Spot</option>
            <option value="Contract" data-id="element-7773">Contract</option>
            <option value="Corporate" data-id="element-7774">Corporate</option>
            <option value="Bank" data-id="element-7775">Bank</option>
            <option value="Custom" data-id="element-7776">Custom</option>
          </select>
        </div>
        {/* Entity/Site Selector */}
        <EntitySiteSelector scope={formData.scope as EntityScope} entityId={formData.entityId} entityName={formData.entityName} siteId={formData.siteId} siteName={formData.siteName} onChange={handleEntitySiteChange} locale={locale} data-id="element-7777" />
        {/* Active Status */}
        <div className="flex items-center" data-id="element-7778">
          <input id="isActive" name="isActive" type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={formData.isActive} onChange={handleChange} data-id="element-7779" />
          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700" data-id="element-7780">
            Is Active
          </label>
        </div>
        {/* Form Actions */}
        <div className="pt-5 border-t border-gray-200" data-id="element-7781">
          <div className="flex justify-end space-x-3" data-id="element-7782">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7783">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7784">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-7785" />
              Save Exchange Rate
            </button>
          </div>
        </div>
      </form>
    </div>;
}